import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams, useLocation } from "react-router-dom";
import {
  FileSignature,
  Eye,
  LayoutList,
  LayoutGrid,
  User,
  PenLine,
  Calendar,
  Building2,
  Clock3,
  ShieldCheck,
  AlertCircle,
} from "lucide-react";
import Pagination from "../../../components/common/Pagination";
import { getSigningRequests } from "../../../api/hrm/signing/signingRequests";
import { formatDateVN, formatDateTimeVN } from "../../../utils/formatUtils";
import SigningFilter from "./SigningFilter";
import SigningMiniDashboard from "./SigningMiniDashboard";
import PendingSigningList from "./PendingSigningList";
import SigningRequestCreateModal from "./SigningRequestCreateModal";
import SigningRequestDetailModal from "./SigningRequestDetailModal";
import {
  STATUS_BADGE,
  STATUS_LABEL,
  DOCUMENT_TYPE_LABEL,
  URGENCY_LABEL,
  URGENCY_OPTIONS,
} from "./signingConstants";

const EMPTY_FILTERS = {
  keyword: "",
  status: "",
  document_type: "",
  signer_role: "",
  urgency: "",
  issued_from: "",
  issued_to: "",
};

const VIEW_STORAGE_KEY = "hrm-signing-view-mode";

const filtersEqual = (a, b) =>
  a.keyword === b.keyword &&
  a.status === b.status &&
  a.document_type === b.document_type &&
  a.signer_role === b.signer_role &&
  a.urgency === b.urgency &&
  a.issued_from === b.issued_from &&
  a.issued_to === b.issued_to;

const urgencyBadgeClass = (urgency) =>
  URGENCY_OPTIONS.find((o) => o.value === urgency)?.color ||
  "bg-slate-100 text-slate-600 border-slate-200";

const PersonMeta = ({ label, name, time, icon: Icon = User, empty = "—" }) => (
  <div className="min-w-0">
    <p className="text-[9px] font-bold uppercase tracking-widest text-gray-400 flex items-center gap-1">
      <Icon size={10} className="text-red-500/80 shrink-0" />
      {label}
    </p>
    <p className="mt-0.5 text-xs font-semibold text-gray-800 truncate">
      {name || empty}
    </p>
    {time ? (
      <p className="mt-0.5 text-[10px] text-gray-400 tabular-nums flex items-center gap-1">
        <Clock3 size={10} className="shrink-0" />
        {formatDateTimeVN(time)}
      </p>
    ) : null}
  </div>
);

const StatusBadges = ({ row }) => (
  <div className="flex flex-wrap gap-1.5">
    <span
      className={`text-[10px] font-bold px-2.5 py-1 rounded-full border ${
        STATUS_BADGE[row.status] || "bg-gray-100 text-gray-600 border-gray-200"
      }`}
    >
      {STATUS_LABEL[row.status] || row.status}
    </span>
    {row.urgency && row.urgency !== "normal" ? (
      <span
        className={`text-[10px] font-bold px-2.5 py-1 rounded-full border inline-flex items-center gap-1 ${urgencyBadgeClass(
          row.urgency
        )}`}
      >
        <AlertCircle size={10} />
        {URGENCY_LABEL[row.urgency] || row.urgency}
      </span>
    ) : null}
  </div>
);

const SigningRequestCard = ({ row, index, page, pageSize, onOpen }) => {
  const signerName =
    row.signed_by_name ||
    (row.signer_role === "CEO" ? row.ceo_approver_name : row.coo_approver_name);

  return (
    <button
      type="button"
      onClick={() => onOpen(row.id)}
      className="w-full text-left group relative overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm hover:border-red-200 hover:shadow-md hover:shadow-red-50/80 hover:-translate-y-0.5 transition-all"
    >
      <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-red-600 to-rose-500 opacity-80" />
      <div className="p-4 pt-5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2 text-[10px] text-gray-400">
              <span className="font-mono font-semibold text-gray-500">
                {row.request_code}
              </span>
              <span className="text-gray-300">·</span>
              <span className="tabular-nums">
                #{(page - 1) * pageSize + index + 1}
              </span>
            </div>
            <h3 className="mt-1 text-sm font-bold text-gray-900 line-clamp-2 group-hover:text-red-700 transition-colors">
              {row.title}
            </h3>
          </div>
          <span className="shrink-0 inline-flex h-9 w-9 items-center justify-center rounded-xl border border-gray-100 bg-gray-50 text-gray-400 group-hover:border-red-200 group-hover:bg-red-50 group-hover:text-red-600 transition-colors">
            <Eye size={16} />
          </span>
        </div>

        <div className="mt-3">
          <StatusBadges row={row} />
        </div>

        <div className="mt-3 flex flex-wrap gap-1.5">
          <span className="text-[10px] font-bold uppercase tracking-wide text-gray-600 px-2 py-1 rounded-lg bg-gray-50 border border-gray-100">
            {DOCUMENT_TYPE_LABEL[row.document_type] || row.document_type || "—"}
          </span>
          <span className="text-[10px] font-bold uppercase tracking-wide text-red-700 px-2 py-1 rounded-lg bg-red-50 border border-red-100 inline-flex items-center gap-1">
            <ShieldCheck size={11} />
            Ký: {row.signer_role}
          </span>
          {row.document_number ? (
            <span className="text-[10px] font-medium text-gray-500 px-2 py-1 rounded-lg bg-white border border-gray-100 font-mono">
              {row.document_number}
            </span>
          ) : null}
        </div>

        <div className="mt-4 grid grid-cols-2 gap-2.5">
          <div className="rounded-xl border border-gray-100 bg-gray-50/80 px-3 py-2.5">
            <PersonMeta
              label="Người tạo"
              name={row.created_by_name}
              time={row.created_at}
            />
          </div>
          <div className="rounded-xl border border-gray-100 bg-gray-50/80 px-3 py-2.5">
            <PersonMeta
              label={row.signed_by_name ? "Người ký" : "Người ký dự kiến"}
              name={signerName}
              time={row.signed_at}
              icon={PenLine}
              empty={row.signer_role}
            />
          </div>
        </div>

        {(row.coo_approver_name || row.coo_approved_at) && (
          <div className="mt-2 rounded-xl border border-amber-100 bg-amber-50/50 px-3 py-2">
            <PersonMeta
              label="COO duyệt"
              name={row.coo_approver_name}
              time={row.coo_approved_at}
              icon={ShieldCheck}
            />
          </div>
        )}

        <div className="mt-3 pt-3 border-t border-gray-100 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-gray-500">
          <span className="inline-flex items-center gap-1">
            <Calendar size={12} className="text-red-500" />
            Ban hành:{" "}
            <strong className="text-gray-700 font-semibold">
              {row.issued_date ? formatDateVN(row.issued_date) : "—"}
            </strong>
          </span>
          {row.issuing_department ? (
            <span className="inline-flex items-center gap-1 min-w-0">
              <Building2 size={12} className="text-red-500 shrink-0" />
              <span className="truncate">{row.issuing_department}</span>
            </span>
          ) : null}
        </div>
      </div>
    </button>
  );
};

const SigningRequestListPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const notificationTarget =
    location.state?.notificationTarget?.moduleCode === "hrm.signing"
      ? location.state.notificationTarget
      : null;

  const [records, setRecords] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    signed: 0,
    completed: 0,
  });
  const [showCreate, setShowCreate] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [viewMode, setViewMode] = useState(() => {
    try {
      const saved = localStorage.getItem(VIEW_STORAGE_KEY);
      return saved === "cards" || saved === "list" ? saved : "list";
    } catch {
      return "list";
    }
  });
  const filtersRef = useRef(filters);
  const didInitFetchRef = useRef(false);
  const pageSize = 10;

  const effectiveView = isMobile ? "cards" : viewMode;

  const changeViewMode = (mode) => {
    setViewMode(mode);
    try {
      localStorage.setItem(VIEW_STORAGE_KEY, mode);
    } catch {
      /* ignore */
    }
  };

  const loadStats = useCallback(async () => {
    try {
      const [allRes, pendingRes, signedRes, completedRes] = await Promise.all([
        getSigningRequests({ page: 1, limit: 1 }),
        getSigningRequests({ status: "pending", page: 1, limit: 1 }),
        getSigningRequests({ status: "signed", page: 1, limit: 1 }),
        getSigningRequests({ status: "completed", page: 1, limit: 1 }),
      ]);
      setStats({
        total: allRes.total || 0,
        pending: pendingRes.total || 0,
        signed: signedRes.total || 0,
        completed: completedRes.total || 0,
      });
    } catch {
      /* ignore */
    }
  }, []);

  const fetchData = useCallback(async (pageNum, nextFilters) => {
    setLoading(true);
    try {
      const res = await getSigningRequests({
        page: pageNum,
        limit: pageSize,
        status: nextFilters.status || undefined,
        keyword: nextFilters.keyword?.trim() || undefined,
        document_type: nextFilters.document_type || undefined,
        signer_role: nextFilters.signer_role || undefined,
        urgency: nextFilters.urgency || undefined,
        issued_from: nextFilters.issued_from || undefined,
        issued_to: nextFilters.issued_to || undefined,
      });
      setRecords(res.records || []);
      setTotal(res.total || 0);
    } catch {
      setRecords([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleRefresh = () => {
    setRefreshKey((k) => k + 1);
    loadStats();
    fetchData(page, filtersRef.current);
  };

  useEffect(() => {
    loadStats();
  }, [loadStats, refreshKey]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const openId = searchParams.get("open") || notificationTarget?.referenceId;
    if (openId) {
      setDetailId(openId);
      if (searchParams.get("open")) {
        searchParams.delete("open");
        setSearchParams(searchParams, { replace: true });
      }
    }
  }, [searchParams, setSearchParams, notificationTarget?.referenceId]);

  const handleFilterChange = (next) => {
    const same = filtersEqual(filtersRef.current, next);
    filtersRef.current = next;
    setFilters(next);
    setPage(1);

    if (!didInitFetchRef.current || !same) {
      didInitFetchRef.current = true;
      fetchData(1, next);
    }
  };

  const handlePageChange = (nextPage) => {
    setPage(nextPage);
    fetchData(nextPage, filtersRef.current);
  };

  const DASHBOARD_STATUS = {
    total: "",
    pending: "pending",
    signed: "signed",
    completed: "completed",
  };

  const activeDashboardKey =
    filters.status === "pending"
      ? "pending"
      : filters.status === "signed"
        ? "signed"
        : filters.status === "completed"
          ? "completed"
          : filters.status === ""
            ? "total"
            : null;

  const handleDashboardSelect = (key) => {
    if (!(key in DASHBOARD_STATUS)) return;
    const nextStatus = DASHBOARD_STATUS[key];
    const status =
      activeDashboardKey === key && key !== "total" ? "" : nextStatus;
    handleFilterChange({ ...filtersRef.current, status });
  };

  const renderEmpty = (message) => (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-12 text-center text-sm text-gray-400 italic">
      {message}
    </div>
  );

  return (
    <div className="w-full min-h-screen bg-gray-50/30 p-3 pb-24 md:p-8 md:pb-8">
      <div className="mb-4 flex flex-col gap-3 sm:mb-6 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
        <div data-tour="hrm-signing-header">
          <div className="mb-1 flex items-center gap-3 sm:mb-2 sm:gap-4">
            <div className="rounded-2xl border border-red-700/10 bg-red-700/5 p-3 text-red-700 shadow-sm sm:rounded-[1.5rem] sm:p-4">
              <FileSignature className="h-5 w-5 sm:h-6 sm:w-6" />
            </div>
            <div className="min-w-0">
              <h1 className="text-xl font-black uppercase tracking-tight leading-tight text-gray-800 sm:text-2xl">
                Quản lý <span className="text-red-700">Trình ký</span>
              </h1>
              <p className="mt-0.5 text-[10px] font-bold uppercase tracking-[0.16em] text-gray-400 sm:mt-1 sm:tracking-[0.2em]">
                Trình ký văn bản PDF — COO / CEO ký điện tử
              </p>
            </div>
          </div>
        </div>
      </div>

      <SigningMiniDashboard
        stats={stats}
        activeKey={activeDashboardKey}
        onSelect={handleDashboardSelect}
      />

      <SigningFilter
        value={filters}
        onNewClick={() => setShowCreate(true)}
        onFilterChange={handleFilterChange}
        onRefresh={handleRefresh}
      />

      <PendingSigningList
        filters={filters}
        refreshKey={refreshKey}
        onSelect={(item) => setDetailId(item.id)}
      />

      <div className="mb-3 flex flex-wrap items-center justify-between gap-3 px-1">
        <h2 className="font-semibold text-base text-gray-700">
          Danh sách yêu cầu{" "}
          <span className="text-gray-400 font-medium">({total})</span>
        </h2>

        {!isMobile && (
          <div className="inline-flex items-center rounded-full border border-gray-200 bg-white p-1 shadow-sm">
            <button
              type="button"
              onClick={() => changeViewMode("list")}
              className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-[11px] font-bold uppercase tracking-wider transition-all ${
                viewMode === "list"
                  ? "bg-red-600 text-white shadow-sm"
                  : "text-gray-500 hover:text-red-600"
              }`}
            >
              <LayoutList size={14} />
              Danh sách
            </button>
            <button
              type="button"
              onClick={() => changeViewMode("cards")}
              className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-[11px] font-bold uppercase tracking-wider transition-all ${
                viewMode === "cards"
                  ? "bg-red-600 text-white shadow-sm"
                  : "text-gray-500 hover:text-red-600"
              }`}
            >
              <LayoutGrid size={14} />
              Cards
            </button>
          </div>
        )}
      </div>

      {loading ? (
        renderEmpty("Đang tải dữ liệu...")
      ) : records.length === 0 ? (
        renderEmpty("Chưa có yêu cầu trình ký")
      ) : effectiveView === "cards" ? (
        <div className="space-y-4" data-tour="hrm-signing-table">
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
            {records.map((row, index) => (
              <SigningRequestCard
                key={row.id}
                row={row}
                index={index}
                page={page}
                pageSize={pageSize}
                onOpen={setDetailId}
              />
            ))}
          </div>
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <Pagination
              currentPage={page}
              totalRecords={total}
              pageSize={pageSize}
              onPageChange={handlePageChange}
            />
          </div>
        </div>
      ) : (
        <div
          className="bg-white rounded-[2rem] border border-gray-100 shadow-sm overflow-hidden"
          data-tour="hrm-signing-table"
        >
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse min-w-[1100px]">
              <thead className="bg-gradient-to-r from-red-50/70 via-white to-white border-b border-gray-100">
                <tr>
                  <th className="p-4 w-12 text-[11px] font-black text-gray-400 uppercase tracking-widest text-center">
                    STT
                  </th>
                  <th className="p-4 text-[11px] font-black text-gray-400 uppercase tracking-widest min-w-[240px]">
                    Văn bản
                  </th>
                  <th className="p-4 text-[11px] font-black text-gray-400 uppercase tracking-widest">
                    Ban hành
                  </th>
                  <th className="p-4 text-[11px] font-black text-gray-400 uppercase tracking-widest min-w-[150px]">
                    Người tạo
                  </th>
                  <th className="p-4 text-[11px] font-black text-gray-400 uppercase tracking-widest min-w-[150px]">
                    Người ký
                  </th>
                  <th className="p-4 text-[11px] font-black text-gray-400 uppercase tracking-widest">
                    Luồng
                  </th>
                  <th className="p-4 text-[11px] font-black text-gray-400 uppercase tracking-widest">
                    Trạng thái
                  </th>
                  <th className="p-4 w-14 text-[11px] font-black text-gray-400 uppercase tracking-widest text-center">
                    Xem
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {records.map((row, index) => {
                  const signerName =
                    row.signed_by_name ||
                    (row.signer_role === "CEO"
                      ? row.ceo_approver_name
                      : row.coo_approver_name);

                  return (
                    <tr
                      key={row.id}
                      onClick={() => setDetailId(row.id)}
                      className="hover:bg-red-50/40 transition-colors cursor-pointer"
                    >
                      <td className="p-4 text-center text-xs font-semibold text-gray-500 tabular-nums">
                        {(page - 1) * pageSize + index + 1}
                      </td>
                      <td className="p-4 max-w-[320px]">
                        <p className="font-semibold text-gray-800 truncate">
                          {row.title}
                        </p>
                        <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
                          <span className="font-mono text-[11px] text-gray-500">
                            {row.request_code}
                          </span>
                          {row.document_type ? (
                            <>
                              <span className="text-gray-300">·</span>
                              <span className="text-[11px] font-medium text-gray-600">
                                {DOCUMENT_TYPE_LABEL[row.document_type] ||
                                  row.document_type}
                              </span>
                            </>
                          ) : null}
                          {row.document_number ? (
                            <>
                              <span className="text-gray-300">·</span>
                              <span className="text-[11px] font-mono text-gray-500">
                                {row.document_number}
                              </span>
                            </>
                          ) : null}
                        </div>
                        {row.issuing_department ? (
                          <p className="mt-1 text-[10px] text-gray-400 inline-flex items-center gap-1">
                            <Building2 size={10} />
                            {row.issuing_department}
                          </p>
                        ) : null}
                      </td>
                      <td className="p-4">
                        <p className="text-xs font-semibold text-gray-700">
                          {row.issued_date
                            ? formatDateVN(row.issued_date)
                            : "—"}
                        </p>
                        {row.effective_until ? (
                          <p className="mt-0.5 text-[10px] text-gray-400">
                            Hết HL: {formatDateVN(row.effective_until)}
                          </p>
                        ) : null}
                      </td>
                      <td className="p-4">
                        <PersonMeta
                          name={row.created_by_name}
                          time={row.created_at}
                          label="Tạo"
                        />
                      </td>
                      <td className="p-4">
                        <PersonMeta
                          icon={PenLine}
                          label={row.signed_at ? "Đã ký" : "Dự kiến"}
                          name={signerName}
                          time={row.signed_at}
                          empty={row.signer_role || "—"}
                        />
                        {row.coo_approved_at ? (
                          <p className="mt-1 text-[10px] text-amber-700/80">
                            COO duyệt {formatDateTimeVN(row.coo_approved_at)}
                          </p>
                        ) : null}
                      </td>
                      <td className="p-4">
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-red-700 px-2 py-1 rounded-lg bg-red-50 border border-red-100">
                          <ShieldCheck size={11} />
                          {row.signer_role}
                        </span>
                      </td>
                      <td className="p-4">
                        <StatusBadges row={row} />
                      </td>
                      <td className="p-4 text-center">
                        <span className="inline-flex h-8 w-8 items-center justify-center rounded-xl border border-gray-100 text-gray-400 hover:text-red-600 hover:border-red-200 hover:bg-red-50">
                          <Eye size={15} />
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <Pagination
            currentPage={page}
            totalRecords={total}
            pageSize={pageSize}
            onPageChange={handlePageChange}
          />
        </div>
      )}

      <SigningRequestCreateModal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={() => handleRefresh()}
      />

      <SigningRequestDetailModal
        open={!!detailId}
        requestId={detailId}
        onClose={() => setDetailId(null)}
        onUpdated={handleRefresh}
      />
    </div>
  );
};

export default SigningRequestListPage;
